/**
 * GTM dataLayer tracking for the SiteForge funnel.
 *
 * Events (per the tracking spec in /docs, 2026-04-16):
 *   sf_signup            — free account created
 *   sf_design_started    — AI generation kicked off
 *   sf_design_completed  — AI generation finished successfully
 *   sf_deploy_started    — user begins checkout (intent to pay)
 *   sf_purchase          — payment succeeded (THE conversion event)
 *   sf_deploy_failed     — post-payment deploy or publish failure
 *   sf_renewal           — recurring charge succeeded (NOT sent to Ads)
 *
 * Design rules:
 *   - Never push email, phone, name, address, or card data. Internal UUIDs
 *     only (sf_user_id, sf_transaction_id, sf_design_id).
 *   - Every helper is synchronous + fail-silent — tracking must never
 *     break the user flow if GTM / the network is unavailable.
 *   - Currency values are USD dollars (not cents) since Google Ads
 *     conversion values and GA4 `value` expect dollar units.
 */

type DataLayerValue = Record<string, unknown>;

declare global {
  interface Window {
    dataLayer?: DataLayerValue[];
  }
}

const BOT_UA_RE = /HeadlessChrome|Lighthouse|GTmetrix|UptimeRobot|PageSpeed|Speedindex|Bingbot|Googlebot|AhrefsBot|SemrushBot|DataForSeo|Pingdom/i;
const QA_EMAIL_RE = /@creative813\.com$/i;

/**
 * True when the current page-load is bot/QA traffic that must NOT count as
 * a real conversion. Drives the central guard in `push()` plus an exported
 * helper so other modules can short-circuit before doing async work.
 */
export function isAnalyticsBlocked(email?: string): boolean {
  try {
    if (typeof window === 'undefined') return true;
    if (typeof navigator !== 'undefined' && BOT_UA_RE.test(navigator.userAgent || '')) return true;
    const params = new URLSearchParams(window.location.search);
    if (params.get('qa') === '1') return true;
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('sf_qa_session') === '1') return true;
    if (email && QA_EMAIL_RE.test(email.trim())) return true;
  } catch {
    // If we can't read the environment, fall through to "not blocked" so
    // tracking still works in unusual sandboxes.
  }
  return false;
}

function push(payload: DataLayerValue, opts?: { email?: string }): void {
  try {
    if (typeof window === 'undefined') return;
    if (isAnalyticsBlocked(opts?.email)) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  } catch {
    // Swallow — tracking is best-effort, never blocks the flow.
  }
}

/**
 * SHA-256 of `text` as lowercase hex. Used for Enhanced Conversions /
 * Advanced Matching where Google Ads, Bing UET, and Meta Pixel can match
 * a hashed email back to a logged-in account on their side.
 *
 * Returns `undefined` if `crypto.subtle` isn't available (legacy / non-HTTPS
 * contexts) — callers should treat the field as optional.
 */
export async function sha256Hex(text: string): Promise<string | undefined> {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return undefined;
    const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return undefined;
  }
}

export type UseCase = 'redesign' | 'new_site';
export type PlanSlug = 'starter' | 'grow' | 'scale' | 'agency' | string;

export interface SignupParams {
  userId: string;
  source?: 'organic' | 'paid' | 'free_trial' | 'redesign_free' | 'new_free' | 'referral' | 'outreach' | 'partner' | string;
  /**
   * Plain email — used to compute `sf_email_hash` for Enhanced Conversions
   * (Meta / Google Ads / Bing). Hashed via SHA-256 before push, never sent
   * raw. Optional: if absent, `sf_email_hash` is omitted.
   */
  email?: string;
}

/**
 * Fire `sf_signup` AFTER the backend confirms account creation. Caller
 * should only invoke this on a successful response — never on submit-time
 * intent. Source is the server-recorded `signup_source` (see
 * `getSignupSource()` in attribution.ts) so paid + free + redesign-free +
 * new-free are distinguishable.
 *
 * Email is hashed (SHA-256 lowercase trim hex) for Enhanced Conversions
 * and never pushed in plaintext. We still scrub the entire push if the
 * email matches a known QA pattern (see `isAnalyticsBlocked`).
 */
export function trackSignup({ userId, source, email }: SignupParams): void {
  if (!userId) return;
  if (isAnalyticsBlocked(email)) return;
  // sha256 is async; fire the dataLayer push as soon as the digest resolves
  // (typically <1ms in browsers). Don't block the submit flow.
  const fire = (hash?: string) => {
    push(
      {
        event: 'sf_signup',
        sf_user_id: userId,
        sf_signup_source: source || 'organic',
        ...(hash ? { sf_email_hash: hash } : {}),
      },
      { email }
    );
  };
  if (email) {
    sha256Hex(email.toLowerCase().trim()).then(fire).catch(() => fire(undefined));
  } else {
    fire(undefined);
  }
}

export interface EmailConfirmedParams {
  userId: string;
  source?: string;
  email?: string;
}

/**
 * Fire `sf_email_confirmed` after `/api/auth/confirm-email` responds OK.
 * Stronger purchase-intent signal than raw signup (filters out throwaway
 * emails). Once we see steady volume, GTM can promote this to a Google
 * Ads conversion alongside `sf_signup`.
 */
export function trackEmailConfirmed({ userId, source, email }: EmailConfirmedParams): void {
  if (!userId) return;
  if (isAnalyticsBlocked(email)) return;
  const fire = (hash?: string) => {
    push(
      {
        event: 'sf_email_confirmed',
        sf_user_id: userId,
        sf_signup_source: source || 'free_trial',
        ...(hash ? { sf_email_hash: hash } : {}),
      },
      { email }
    );
  };
  if (email) {
    sha256Hex(email.toLowerCase().trim()).then(fire).catch(() => fire(undefined));
  } else {
    fire(undefined);
  }
}

export interface DesignStartedParams {
  userId?: string;
  projectId: string;
  designId?: string;
  useCase: UseCase;
  sourceUrl?: string;
  mode?: 'generate' | 'redesign' | 'edit';
}

export function trackDesignStarted({ userId, projectId, designId, useCase, sourceUrl, mode }: DesignStartedParams): void {
  if (!projectId) return;
  push({
    event: 'sf_design_started',
    sf_user_id: userId,
    sf_project_id: projectId,
    sf_design_id: designId || projectId,
    sf_use_case: useCase,
    sf_source_url: useCase === 'redesign' ? sourceUrl : undefined,
    sf_mode: mode,
  });
}

export interface DesignCompletedParams {
  userId?: string;
  projectId: string;
  designId?: string;
  useCase: UseCase;
  generationSeconds: number;
  mode?: 'generate' | 'redesign' | 'edit';
}

export function trackDesignCompleted({ userId, projectId, designId, useCase, generationSeconds, mode }: DesignCompletedParams): void {
  if (!projectId) return;
  push({
    event: 'sf_design_completed',
    sf_user_id: userId,
    sf_project_id: projectId,
    sf_design_id: designId || projectId,
    sf_use_case: useCase,
    sf_generation_seconds: Math.max(0, Math.round(generationSeconds)),
    sf_mode: mode,
  });
}

export interface DeployStartedParams {
  userId?: string;
  projectId?: string;
  designId?: string;
  planIntent: PlanSlug;
}

export function trackDeployStarted({ userId, projectId, designId, planIntent }: DeployStartedParams): void {
  if (!planIntent) return;
  push({
    event: 'sf_deploy_started',
    sf_user_id: userId,
    sf_project_id: projectId,
    sf_design_id: designId,
    sf_plan_intent: planIntent,
  });
}

export interface PurchaseParams {
  userId?: string;
  transactionId: string;
  plan: PlanSlug;
  planMonthly: number;
  setupFee: number;
  addons?: string[];
  addonMonthly?: number;
  totalFirstPayment: number;
  /**
   * Exact amount captured by Authorize.net in the transaction tied to
   * `transactionId` — setup + one-time addon fees + domain registration,
   * discounted by any setup-level promo. Recurring first-month revenue is
   * NOT included because it's billed 30 days later by the cron worker as a
   * separate transaction. Smart Bidding should optimise against realised
   * revenue, so this is what we push into `value`.
   */
  amountChargedToday: number;
  useCase?: UseCase;
  projectId?: string;
}

export function trackPurchase({
  userId,
  transactionId,
  plan,
  planMonthly,
  setupFee,
  addons,
  addonMonthly,
  totalFirstPayment,
  amountChargedToday,
  useCase,
  projectId,
}: PurchaseParams): void {
  if (!transactionId) return;
  push({
    event: 'sf_purchase',
    sf_user_id: userId,
    sf_transaction_id: transactionId,
    sf_plan: plan,
    sf_plan_monthly: round2(planMonthly),
    sf_setup_fee: round2(setupFee),
    sf_addons: addons || [],
    sf_addon_monthly: round2(addonMonthly || 0),
    sf_total_first_payment: round2(totalFirstPayment),
    sf_amount_charged_today: round2(amountChargedToday),
    sf_use_case: useCase,
    sf_project_id: projectId,
    // GA4 / Google Ads standard keys. `value` is the realised charge today
    // (setup + upfront addons + domain − setup promo), which is exactly what
    // Authorize.net captured. Recurring monthly revenue bills later and fires
    // its own future event (not sent to Ads), so including it here would
    // over-report realised ROAS to Smart Bidding.
    currency: 'USD',
    value: round2(amountChargedToday),
    transaction_id: transactionId,
  });
}

export interface DeployFailedParams {
  userId?: string;
  transactionId?: string;
  projectId?: string;
  reason: string;
}

export function trackDeployFailed({ userId, transactionId, projectId, reason }: DeployFailedParams): void {
  push({
    event: 'sf_deploy_failed',
    sf_user_id: userId,
    sf_transaction_id: transactionId,
    sf_project_id: projectId,
    sf_failure_reason: (reason || 'unknown').toString().slice(0, 80),
  });
}

export interface RenewalParams {
  userId?: string;
  transactionId: string;
  plan: PlanSlug;
  amount: number;
}

/** Recurring charge tracker — fires to GA4 for revenue reporting only. NOT a Google Ads conversion (would double-count). */
export function trackRenewal({ userId, transactionId, plan, amount }: RenewalParams): void {
  if (!transactionId) return;
  push({
    event: 'sf_renewal',
    sf_user_id: userId,
    sf_transaction_id: transactionId,
    sf_plan: plan,
    sf_plan_monthly: round2(amount),
    currency: 'USD',
    value: round2(amount),
  });
}

function round2(n: number | undefined): number {
  if (n == null || Number.isNaN(n)) return 0;
  return Math.round(n * 100) / 100;
}
